import React, { useContext, useMemo } from 'react'
import { getEnergyMode } from '@vacationtracker/shared/functions/user-insights/determine-energy-level'
import { DARK_THEME } from '@vacationtracker/shared/constants/ThemeSetting'
import { ThemeContext } from '../../../../context/themeContext'

// Import light theme images as React components
import EnergyImage0_12_Light from '../../../../assets/images/energy-levels/light/0-12.svg'
import EnergyImage13_24_Light from '../../../../assets/images/energy-levels/light/13-24.svg'
import EnergyImage25_34_Light from '../../../../assets/images/energy-levels/light/25-34.svg'
import EnergyImage35_44_Light from '../../../../assets/images/energy-levels/light/35-44.svg'
import EnergyImage45_51_Light from '../../../../assets/images/energy-levels/light/45-51.svg'
import EnergyImage52_58_Light from '../../../../assets/images/energy-levels/light/52-58.svg'
import EnergyImage59_64_Light from '../../../../assets/images/energy-levels/light/59-64.svg'
import EnergyImage65_71_Light from '../../../../assets/images/energy-levels/light/65-71.svg'
import EnergyImage72_78_Light from '../../../../assets/images/energy-levels/light/72-78.svg'
import EnergyImage79_84_Light from '../../../../assets/images/energy-levels/light/79-84.svg'
import EnergyImage85_94_Light from '../../../../assets/images/energy-levels/light/85-94.svg'
import EnergyImage95_100_Light from '../../../../assets/images/energy-levels/light/95-100.svg'

// Import dark theme images as React components
import EnergyImage0_12_Dark from '../../../../assets/images/energy-levels/dark/0-12.svg'
import EnergyImage13_24_Dark from '../../../../assets/images/energy-levels/dark/13-24.svg'
import EnergyImage25_34_Dark from '../../../../assets/images/energy-levels/dark/25-34.svg'
import EnergyImage35_44_Dark from '../../../../assets/images/energy-levels/dark/35-44.svg'
import EnergyImage45_51_Dark from '../../../../assets/images/energy-levels/dark/45-51.svg'
import EnergyImage52_58_Dark from '../../../../assets/images/energy-levels/dark/52-58.svg'
import EnergyImage59_64_Dark from '../../../../assets/images/energy-levels/dark/59-64.svg'
import EnergyImage65_71_Dark from '../../../../assets/images/energy-levels/dark/65-71.svg'
import EnergyImage72_78_Dark from '../../../../assets/images/energy-levels/dark/72-78.svg'
import EnergyImage79_84_Dark from '../../../../assets/images/energy-levels/dark/79-84.svg'
import EnergyImage85_94_Dark from '../../../../assets/images/energy-levels/dark/85-94.svg'
import EnergyImage95_100_Dark from '../../../../assets/images/energy-levels/dark/95-100.svg'

/**
 * Energy level image range configuration
 * Each entry defines the minimum threshold and corresponding image components for both themes
 * Ranges are: 0-12, 13-24, 25-34, 35-44, 45-51, 52-58, 59-64, 65-71, 72-78, 79-84, 85-94, 95-100
 */
type EnergyImageComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>

interface EnergyImageRange {
  minThreshold: number
  lightImage: EnergyImageComponent
  darkImage: EnergyImageComponent
}

const ENERGY_IMAGE_RANGES: readonly EnergyImageRange[] = [
  { minThreshold: 95, lightImage: EnergyImage95_100_Light, darkImage: EnergyImage95_100_Dark },
  { minThreshold: 85, lightImage: EnergyImage85_94_Light, darkImage: EnergyImage85_94_Dark },
  { minThreshold: 79, lightImage: EnergyImage79_84_Light, darkImage: EnergyImage79_84_Dark },
  { minThreshold: 72, lightImage: EnergyImage72_78_Light, darkImage: EnergyImage72_78_Dark },
  { minThreshold: 65, lightImage: EnergyImage65_71_Light, darkImage: EnergyImage65_71_Dark },
  { minThreshold: 59, lightImage: EnergyImage59_64_Light, darkImage: EnergyImage59_64_Dark },
  { minThreshold: 52, lightImage: EnergyImage52_58_Light, darkImage: EnergyImage52_58_Dark },
  { minThreshold: 45, lightImage: EnergyImage45_51_Light, darkImage: EnergyImage45_51_Dark },
  { minThreshold: 35, lightImage: EnergyImage35_44_Light, darkImage: EnergyImage35_44_Dark },
  { minThreshold: 25, lightImage: EnergyImage25_34_Light, darkImage: EnergyImage25_34_Dark },
  { minThreshold: 13, lightImage: EnergyImage13_24_Light, darkImage: EnergyImage13_24_Dark },
  { minThreshold: 0, lightImage: EnergyImage0_12_Light, darkImage: EnergyImage0_12_Dark },
] as const

/**
 * Gets the appropriate energy level image component based on score and theme
 * @param score - Energy score (0-100)
 * @param isDark - Whether dark theme is active
 * @returns React component for the corresponding energy level image
 */
const getEnergyImage = (score: number, isDark: boolean): EnergyImageComponent => {
  // Clamp score to valid range
  const clampedScore = Math.max(0, Math.min(100, score))

  // Find the first range where the score meets or exceeds the threshold
  const matchingRange = ENERGY_IMAGE_RANGES.find(range => clampedScore >= range.minThreshold)

  // Fallback to the lowest range (should never happen due to the 0 threshold, but TypeScript safety)
  return matchingRange
    ? (isDark ? matchingRange.darkImage : matchingRange.lightImage)
    : ENERGY_IMAGE_RANGES[ENERGY_IMAGE_RANGES.length - 1].lightImage
}

interface EnergyLevelImageProps {
  score: number
}

const EnergyLevelImage: React.FC<EnergyLevelImageProps> = ({ score }) => {
  const { theme } = useContext(ThemeContext)
  const isDarkTheme = theme === DARK_THEME
  const energyMode = getEnergyMode(score)

  // Memoize the image component selection to avoid recalculation on every render
  const ImageComponent = useMemo(
    () => getEnergyImage(score, isDarkTheme),
    [score, isDarkTheme]
  )

  return (
    <ImageComponent
      aria-label={`Energy level ${score}`}
      className={`energy-level-image energy-level-image--${energyMode}`}
    />
  )
}

export default EnergyLevelImage
